import type {
    ListingBedroomConfigurationBedroom,
    ListingKeyCollection,
    ListingPolicyInternetPolicyDocument,
    ListingPolicyParkingPolicyDocument,
    ListingPolicyPetPolicyDocument
} from "@hosttools/core/generated/models";
import {ImageTags} from "@hosttools/core/models/listing.constant";
import {type Listing} from "@hosttools/frontend/models/listing";
import currencyToSymbolMap from "currency-symbol-map/map";

import type {NearbyAmenityType} from "./type";

import type {OptionType} from "@/client/components/Select";

export type Fee = NonNullable<Listing["fees"]>[number];

export const spaceOptions: OptionType<Required<Listing>["spaceUnit"]>[] = [
    {
        label: "Square Meters",
        value: "SQ_M"
    },
    {
        label: "Square Feet",
        value: "SQ_FT"
    }
];

export const nearbyAmenitiesOptions: OptionType<NearbyAmenityType["attributeId"]>[] = [
    {
        value: "ACC1",
        label: "Airport"
    },
    {
        value: "ACC203",
        label: "Ferry"
    },
    {
        value: "ACC65",
        label: "Shopping"
    },
    {
        value: "HAC199",
        label: "Railway"
    },
    {
        value: "RST5",
        label: "Beach"
    }
];

export const propertyTypeOptions: OptionType<Required<Listing>["propertyType"]>[] = [
    {
        value: "PCT101",
        label: "Townhome"
    },
    {
        value: "PCT102",
        label: "Single Room"
    },
    {
        value: "PCT103",
        label: "Double Room"
    },
    {
        value: "PCT104",
        label: "Twin"
    },
    {
        value: "PCT105",
        label: "Twin/Double"
    },
    {
        value: "PCT106",
        label: "Triple Room"
    },
    {
        value: "PCT107",
        label: "Quadruple"
    },
    {
        value: "PCT108",
        label: "Family"
    },
    {
        value: "PCT109",
        label: "Suite"
    },
    {
        value: "PCT110",
        label: "Studio"
    },
    {
        value: "PCT111",
        label: "Bungalow"
    },
    {
        value: "PCT112",
        label: "Private room"
    },
    {
        value: "PCT113",
        label: "Shared room"
    },
    {
        value: "PCT114",
        label: "Cottage"
    },
    {
        value: "PCT115",
        label: "Apart Hotel"
    },
    {
        value: "PCT116",
        label: "Narrow boat"
    },
    {
        value: "PCT117",
        label: "Riad"
    },
    {
        value: "PCT118",
        label: "Shepherd Hut"
    },
    {
        value: "PCT119",
        label: "Tipi"
    },
    {
        value: "PCT12",
        label: "Cruise"
    },
    {
        value: "PCT120",
        label: "Tower"
    },
    {
        value: "PCT121",
        label: "Tree house"
    },
    {
        value: "PCT122",
        label: "Trullo"
    },
    {
        value: "PCT123",
        label: "Watermill"
    },
    {
        value: "PCT124",
        label: "Windmill"
    },
    {
        value: "PCT125",
        label: "Yacht"
    },
    {
        value: "PCT126",
        label: "Yurt"
    },
    {
        value: "PCT127",
        label: "Log Cabin"
    },
    {
        value: "PCT128",
        label: "Penthouse"
    },
    {
        value: "PCT14",
        label: "Ferry"
    },
    {
        value: "PCT15",
        label: "Guest farm"
    },
    {
        value: "PCT16",
        label: "Guest house limited service"
    },
    {
        value: "PCT18",
        label: "Holiday resort"
    },
    {
        value: "PCT19",
        label: "Hostel"
    },
    {
        value: "PCT20",
        label: "Hotel"
    },
    {
        value: "PCT21",
        label: "Inn"
    },
    {
        value: "PCT22",
        label: "Lodge"
    },
    {
        value: "PCT23",
        label: "Meeting resort"
    },
    {
        value: "PCT25",
        label: "Mobile-home"
    },
    {
        value: "PCT26",
        label: "Monastery"
    },
    {
        value: "PCT27",
        label: "Motel"
    },
    {
        value: "PCT28",
        label: "Ranch"
    },
    {
        value: "PCT29",
        label: "Residential apartment"
    },
    {
        value: "PCT3",
        label: "Apartment"
    },
    {
        value: "PCT30",
        label: "Resort"
    },
    {
        value: "PCT31",
        label: "Sailing ship"
    },
    {
        value: "PCT32",
        label: "Self catering accommodation"
    },
    {
        value: "PCT33",
        label: "Tent"
    },
    {
        value: "PCT34",
        label: "Vacation home"
    },
    {
        value: "PCT35",
        label: "Villa"
    },
    {
        value: "PCT36",
        label: "Wildlife reserve"
    },
    {
        value: "PCT37",
        label: "Castle"
    },
    {
        value: "PCT4",
        label: "Bed and breakfast"
    },
    {
        value: "PCT40",
        label: "Pension"
    },
    {
        value: "PCT41",
        label: "Ski Chalet"
    },
    {
        value: "PCT44",
        label: "Boatel"
    },
    {
        value: "PCT45",
        label: "Boutique"
    },
    {
        value: "PCT46",
        label: "Efficiency/studio"
    },
    {
        value: "PCT5",
        label: "Cabin or bungalow"
    },
    {
        value: "PCT50",
        label: "Recreational vehicle park"
    },
    {
        value: "PCT51",
        label: "Charm hotel"
    },
    {
        value: "PCT52",
        label: "Manor"
    },
    {
        value: "PCT6",
        label: "Campground"
    },
    {
        value: "PCT7",
        label: "Chalet"
    },
    {
        value: "PCT8",
        label: "Condominium"
    }
];

export const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;

export const currencyOptions: OptionType<string>[] = Object.keys(currencyToSymbolMap).map(code => ({
    value: code,
    label: `${code} (${currencyToSymbolMap[code]})`
}));

export const checkinMethodOptions: OptionType<Required<ListingKeyCollection>["checkInMethod"]>[] = [
    {
        value: "doorman",
        label: "Doorman"
    },
    {
        value: "lock_box",
        label: "Lock box"
    },
    {
        value: "smart_lock",
        label: "Smart lock"
    },
    {
        value: "keypad",
        label: "Keypad"
    },
    {
        value: "in_person_meet",
        label: "In person meet"
    },
    {
        value: "other",
        label: "Other"
    },
    {
        value: "front_desk",
        label: "Front desk"
    },
    {
        value: "secret_spot",
        label: "Secret spot"
    },
    {
        value: "instruction_contact_us",
        label: "Instruction contact us"
    }
];

export const kindOfInternetOptions: OptionType<
    Required<ListingPolicyInternetPolicyDocument>["kindOfInternet"]
>[] = [
    {
        value: "WiFi",
        label: "WiFi"
    },
    {value: "Wired", label: "Wired"}
];

export const availableOfInternetOptions: OptionType<
    Required<ListingPolicyInternetPolicyDocument>["availableInternet"]
>[] = [
    {
        value: "AllAreas",
        label: "All areas"
    },
    {value: "BusinessCenter", label: "Business center"},
    {value: "SomeRooms", label: "Some rooms"}
];

export const allowedPetOptions: OptionType<ListingPolicyPetPolicyDocument["allowedPets"]>[] = [
    {
        value: "Allowed",
        label: "Allowed"
    },
    {value: "AllowedOnRequest", label: "Allowed on request"},
    {value: "NotAllowed", label: "Not allowed"}
];

export const locatedParkingOptions: OptionType<
    Required<ListingPolicyParkingPolicyDocument>["locatedParking"]
>[] = [
    {
        value: "OnSite",
        label: "On Site"
    },
    {value: "Nearby", label: "Nearby"}
];

export const timeCostParkingOptions: OptionType<
    Required<ListingPolicyParkingPolicyDocument>["timeCostParking"]
>[] = [
    {
        value: "PerHour",
        label: "Per Hour"
    },
    {value: "PerWeek", label: "Per week"},
    {value: "PerStay", label: "Per stay"},
    {value: "PerDay", label: "Per day"}
];

export const reservationParkingOptions: OptionType<
    Required<ListingPolicyParkingPolicyDocument>["necessaryReservationParking"]
>[] = [
    {
        value: "NoReservationNeeded",
        label: "No reservation needed"
    },
    {value: "NotPossible", label: "Not possible"},
    {value: "ReservationNeeded", label: "Reservation needed"}
];

export const bedTypeOptions: OptionType<ListingBedroomConfigurationBedroom["type"]>[] = [
    {
        value: "Bedroom",
        label: "Bedroom"
    },
    {
        value: "Living Room",
        label: "Living Room"
    }
];

export const tagOptions = ImageTags.map(tag => ({
    value: tag.Key,
    label: tag.Value
}));

export const bookingLeadTimeOptions = Array.from(Array(8)).map((e, index) => ({
    label:
        index === 0
            ? "Same day"
            : `At least ${
                  index === 7 ? "1 week" : `${index} ${index === 1 ? "day" : "days"}`
              } notice`,
    value: index
}));
